<script setup>
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import i18next from 'i18next';
import { computed, ref } from 'vue';

const props = defineProps({
    filters: {
        type: Object,
        required: true
    },
    sortBy: {
        type: String,
        required: true
    },
    authorIds: {
        type: Array,
        required: true
    },
    date: {
        type: Array,
        required: true
    },
    isAnyFilterActive: {
        type: Boolean,
        required: false
    }
});

const emit = defineEmits(['update:sortBy', 'update:authorIds', 'update:date', 'reset']);

const filterOpened = ref(false);

const sortByOptions = [
    { value: 'published_at:desc', label: i18next.t('newest_first') },
    { value: 'published_at:asc', label: i18next.t('oldest_first') }
];
const calendarLocale = i18next.language || 'de';
const calendarModeltype = 'yyyy-MM-dd';

const localSortBy = computed({
    get: () => props.sortBy,
    set: (value) => emit('update:sortBy', value)
});

const selectAuthor = (userId) => {
    const newAuthorIds = [...props.authorIds];
    const index = newAuthorIds.indexOf(userId);

    if (index > -1) {
        newAuthorIds.splice(index, 1);
    } else {
        newAuthorIds.push(userId);
    }

    emit('update:authorIds', newAuthorIds);
};

const setDate = (newDate) => {
    emit('update:date', newDate);
};

const resetFilters = () => {
    emit('reset');
    filterOpened.value = false;
};
</script>

<template>
    <div v-click-outside="() => (filterOpened = false)" class="w-full">
        <button :class="[
            'relative bg-white w-full flex flex-row items-center gap-4 py-2 px-2 rounded-b-lg lg:rounded-lg shadow-lg border-2 text-blue focus:border-blue hover:border-blue/70 transition-all ease-in-out',
            { 'border-blue': props.isAnyFilterActive, 'border-gray-300': !props.isAnyFilterActive }
        ]" @click="filterOpened = !filterOpened">
            <Icon type="Filter" class="fill-current" viewBox="0 0 21 20" width="21" height="20" />
            <p class="py-1">{{ $t('plain_filter') }}</p>
            <button v-if="isAnyFilterActive" class="p-2 py-0.5 bg-blue text-white rounded-md absolute right-2"
                @click="resetFilters()">
                {{ $t('plain_reset') }}
            </button>
        </button>
        <TransitionExpand v-show="filterOpened" class="absolute right-0 z-50 w-full mt-4">
            <div class="mt-2 rounded-xl shadow-xl p-2 bg-white flex flex-col">
                <div class="flex flex-col lg:flex-row gap-4 h-[20rem] overflow-y-auto">
                    <div class="w-full lg:w-1/3">
                        <p>{{ $t('sort_by_date') }}</p>
                        <Select v-model:selected="localSortBy" name="sort_by" :no-value-allowed="true"
                            :form-component-id="$.uid" :label="$t('sort_by')" :options="sortByOptions"
                            :multiple="false">
                        </Select>
                    </div>

                    <div v-if="filters.users?.data" class="w-full lg:w-1/3">
                        <p>{{ $t('filter_by_authors') }}</p>
                        <div class="overflow-y-auto max-h-[20rem] w-full">
                            <div v-for="user in filters.users.data" :key="user.id">
                                <Checkbox :name="user.first_name + ' ' + user.last_name"
                                    :label="user.first_name + ' ' + user.last_name" :value="authorIds.includes(user.id)"
                                    @update:value="selectAuthor(user.id)" />
                            </div>
                        </div>
                    </div>

                    <div class="w-full lg:w-1/3">
                        <p>{{ $t('filter_by_date') }}</p>
                        <Datepicker :format="calendarModeltype" :model-value="date" :locale="calendarLocale"
                            :select-text="$t('plain_select')" :enable-time-picker="false" :range="true" inline
                            auto-apply :monday-first="true" @update:model-value="setDate" />
                    </div>
                </div>

                <hr class="border-gray-200 w-full my-2" />

                <div class="text-right">
                    <p class="text-blue cursor-pointer" @click="resetFilters()">{{ $t('plain_reset') }}</p>
                </div>
            </div>
        </TransitionExpand>
    </div>
</template>
